/**
 * PublicFillPage — the unauthenticated questionnaire fill page.
 *
 * Rendered by App.tsx for the pre-auth route `/q/{token}` (BEFORE the Cognito
 * gate). A patient opens the emailed link, fills the questionnaire, submits.
 *
 * SECURITY (spec §4 + security review M2):
 *  - Loads ZERO third-party resources (no external fonts/images/scripts) — all
 *    styles are local (PublicFill.css) and inline; theme tokens are inlined so
 *    the page is fully self-contained.
 *  - The fill token arrives in the URL path `/q/{token}`. On mount we read it
 *    into a ref and IMMEDIATELY `history.replaceState` the visible URL down to
 *    `/q`, so the token is never bookmarked, shown, or referrer-leaked.
 *  - The token travels to the API ONLY in the JSON POST body — never a path,
 *    query string, <a href>, <img src>, or anything that could referrer-leak.
 *  - NO auth imports (no Cognito, no AuthProvider). The lookup response carries
 *    ZERO PHI — only {name, timeframe, items, scale, lang} — so nothing here
 *    displays patient/therapist data.
 *
 * Localization: the page is localized by the SEND's `lang` (en/hu) returned
 * from lookup, NOT the app's i18n language (a logged-out visitor has no app
 * language pref). We use `i18n.getFixedT(lang, "translation")` so the strings
 * come from the single source of truth (en.ts / hu.ts `publicFill.*`) WITHOUT
 * mutating the global app language.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE } from "../../config";
import i18n from "../../i18n";
import "./PublicFill.css";

type ScaleStep = { value: number; label: string };
type Item = { id: string; text: string };
type Instrument = {
  name: string;
  timeframe: string;
  items: Item[];
  scale: ScaleStep[];
  lang: string;
};

type Phase = "loading" | "form" | "submitting" | "submitted";
// Distinct API error codes (spec §4): 404 not_found, 410 expired,
// 409 already_submitted. Anything else collapses to "generic".
type ErrorCode = "not_found" | "expired" | "already_submitted" | "generic";

const LOOKUP_URL = `${API_BASE}/public/questionnaires/lookup`;
const SUBMIT_URL = `${API_BASE}/public/questionnaires/submit`;

/** Read the fill token from `/q/{token}` and strip it out of the visible URL. */
function readAndStripToken(): string {
  const path = window.location.pathname || "";
  // Everything after the `/q/` prefix is the opaque token.
  const raw = path.startsWith("/q/") ? path.slice("/q/".length) : "";
  // M2: strip the token from the address bar FIRST — before any parsing that
  // could throw (e.g. malformed percent-encoding) — so a bad link can never
  // leave the token segment visible in history/referrer.
  try {
    window.history.replaceState(window.history.state, "", "/q");
  } catch (e) {
    // A failure here is loud (console) but must not block the fill flow; the
    // page still functions, only the URL-scrub cosmetic degrades.
    console.error("[PublicFill] failed to strip token from URL", e);
  }
  // Real tokens are urlsafe base64 (no `%`); tolerate a malformed link rather
  // than throwing out of the mount effect.
  let token = raw;
  try {
    token = decodeURIComponent(raw);
  } catch (e) {
    console.error("[PublicFill] malformed token encoding", e);
  }
  return token.trim();
}

function errorCodeForStatus(status: number): ErrorCode {
  if (status === 404) return "not_found";
  if (status === 410) return "expired";
  if (status === 409) return "already_submitted";
  return "generic";
}

const PublicFillPage: React.FC = () => {
  // The app's current language is the best guess for the pre-lookup loading
  // text; once lookup returns we switch to the send's lang.
  const [lang, setLang] = useState<string>(() => (i18n.language === "hu" ? "hu" : "en"));
  const [phase, setPhase] = useState<Phase>("loading");
  const [errorCode, setErrorCode] = useState<ErrorCode | null>(null);
  const [instrument, setInstrument] = useState<Instrument | null>(null);
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [showValidation, setShowValidation] = useState(false);

  const tokenRef = useRef<string>("");
  const startedRef = useRef(false);

  // Strings bound to the chosen language, sourced from en.ts / hu.ts. Rebinds
  // when `lang` flips from the app default to the send's lang.
  const t = useMemo(() => i18n.getFixedT(lang === "hu" ? "hu" : "en", "translation"), [lang]);

  useEffect(() => {
    // Guard against React StrictMode's double effect invocation so the token is
    // read/stripped and the lookup fires exactly once.
    if (startedRef.current) return;
    startedRef.current = true;

    const token = readAndStripToken();
    tokenRef.current = token;

    if (!token) {
      setErrorCode("not_found");
      setPhase("loading"); // stays in error render (errorCode set)
      return;
    }

    (async () => {
      try {
        const resp = await fetch(LOOKUP_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ token }),
        });
        if (!resp.ok) {
          setErrorCode(errorCodeForStatus(resp.status));
          return;
        }
        const data = (await resp.json()) as Instrument;
        setInstrument(data);
        if (data.lang === "hu" || data.lang === "en") setLang(data.lang);
        setPhase("form");
      } catch (e) {
        console.error("[PublicFill] lookup failed", e);
        setErrorCode("generic");
      }
    })();
  }, []);

  const setAnswer = useCallback((itemId: string, value: number) => {
    setResponses((prev) => ({ ...prev, [itemId]: value }));
  }, []);

  const missingIds = useMemo(() => {
    if (!instrument) return [];
    return instrument.items.filter((it) => responses[it.id] === undefined).map((it) => it.id);
  }, [instrument, responses]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!instrument) return;
      // Every question is required — loud inline validation, no submit until
      // all are answered.
      if (missingIds.length > 0) {
        setShowValidation(true);
        return;
      }
      setPhase("submitting");
      try {
        const resp = await fetch(SUBMIT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ token: tokenRef.current, responses }),
        });
        if (!resp.ok) {
          setErrorCode(errorCodeForStatus(resp.status));
          return;
        }
        setPhase("submitted");
      } catch (err) {
        console.error("[PublicFill] submit failed", err);
        setErrorCode("generic");
      }
    },
    [instrument, missingIds, responses]
  );

  // ---- Error render (any state where errorCode is set) ----
  if (errorCode) {
    const titleKey =
      errorCode === "not_found"
        ? "publicFill.errorNotFoundTitle"
        : errorCode === "expired"
        ? "publicFill.errorExpiredTitle"
        : errorCode === "already_submitted"
        ? "publicFill.errorUsedTitle"
        : "publicFill.errorGenericTitle";
    const bodyKey =
      errorCode === "not_found"
        ? "publicFill.errorNotFoundBody"
        : errorCode === "expired"
        ? "publicFill.errorExpiredBody"
        : errorCode === "already_submitted"
        ? "publicFill.errorUsedBody"
        : "publicFill.errorGenericBody";
    return (
      <div className="pf-root">
        <div className="pf-card pf-card--message" role="alert">
          <div className="pf-emoji" aria-hidden="true">
            &#x26A0;
          </div>
          <h1 className="pf-title">{t(titleKey)}</h1>
          <p className="pf-message">{t(bodyKey)}</p>
        </div>
      </div>
    );
  }

  if (phase === "submitted") {
    return (
      <div className="pf-root">
        <div className="pf-card pf-card--message" role="status">
          <div className="pf-emoji" aria-hidden="true">
            &#x2705;
          </div>
          <h1 className="pf-title">{t("publicFill.thanksTitle")}</h1>
          <p className="pf-message">{t("publicFill.thanksBody")}</p>
        </div>
      </div>
    );
  }

  if (phase === "loading" || !instrument) {
    return (
      <div className="pf-root">
        <div className="pf-card pf-card--message">
          <p className="pf-message">{t("publicFill.loading")}</p>
        </div>
      </div>
    );
  }

  const submitting = phase === "submitting";

  return (
    <div className="pf-root">
      <form className="pf-card" onSubmit={handleSubmit} noValidate>
        <header className="pf-header">
          <h1 className="pf-title">{instrument.name}</h1>
          {instrument.timeframe ? <p className="pf-timeframe">{instrument.timeframe}</p> : null}
        </header>

        {showValidation && missingIds.length > 0 ? (
          <div className="pf-formError" role="alert">
            {t("publicFill.requiredAll")}
          </div>
        ) : null}

        <ol className="pf-items">
          {instrument.items.map((item, idx) => {
            const isMissing = showValidation && responses[item.id] === undefined;
            return (
              <li
                key={item.id}
                className={`pf-item${isMissing ? " pf-item--missing" : ""}`}
                data-item-id={item.id}
              >
                <fieldset className="pf-fieldset">
                  <legend className="pf-question">
                    <span className="pf-qnum">{idx + 1}.</span> {item.text}
                  </legend>
                  <div className="pf-scale">
                    {instrument.scale.map((step) => {
                      const inputId = `pf-${item.id}-${step.value}`;
                      return (
                        <label key={step.value} className="pf-option" htmlFor={inputId}>
                          <input
                            id={inputId}
                            type="radio"
                            name={`item-${item.id}`}
                            value={step.value}
                            checked={responses[item.id] === step.value}
                            onChange={() => setAnswer(item.id, step.value)}
                            disabled={submitting}
                          />
                          <span className="pf-optionLabel">{step.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {isMissing ? (
                    <p className="pf-fieldError">{t("publicFill.requiredField")}</p>
                  ) : null}
                </fieldset>
              </li>
            );
          })}
        </ol>

        <button className="pf-submit" type="submit" disabled={submitting}>
          {submitting ? t("publicFill.submitting") : t("publicFill.submit")}
        </button>
      </form>
    </div>
  );
};

export default PublicFillPage;
